<template>
  <v-card flat class="rmr-table-shell">
    <rmr-table-header>
      <template #heading>
        <div class="rmr-table-shell__eyebrow">News Radar</div>
        <div class="rmr-table-shell__title">Historique des demandes</div>
      </template>
      <template #controls>
        <div class="rmr-table-shell__filter-item srq-history__site">
          <span class="rmr-table-shell__filter-label">Site</span>
          <rmr-autocomplete
            v-model="filters.siteId"
            :items="siteList"
            item-text="name"
            item-value="id"
            label="Tous les sites"
            clearable
          />
        </div>
        <div class="rmr-table-shell__filter-item">
          <span class="rmr-table-shell__filter-label">Statut</span>
          <rmr-select
            v-model="filters.status"
            :items="statusOptions"
            item-text="label"
            item-value="value"
            label="Tous"
            clearable
          />
        </div>
        <rmr-action-button
          variant="ghost"
          :icon="icons.mdiRefresh"
          label="Rafraîchir"
          :loading="loading"
          @click="refresh"
        />
      </template>
    </rmr-table-header>

    <v-card-text class="rmr-table-shell__body">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
        item-key="id"
        class="rmr-data-table"
        @click:row="openDetail"
      >
        <template #[`item.created_at`]="{ item }">
          {{ formatDateHumanReadableDayHoursMinutes(item.created_at) }}
        </template>

        <template #[`item.site`]="{ item }">
          {{ item.site ? item.site.name : "—" }}
        </template>

        <template #[`item.media_type`]="{ item }">
          {{ mediaTypeLabel(item.media_type) }}
        </template>

        <template #[`item.counts`]="{ item }">
          <span class="srq-history__counts">
            <strong class="srq-history__ok">{{ item.accepted_count }}</strong>
            <span class="srq-history__sep">/</span>
            <strong class="srq-history__ko">{{ item.rejected_count }}</strong>
            <template v-if="item.error_count">
              <span class="srq-history__sep">/</span>
              <strong class="srq-history__err">{{ item.error_count }}</strong>
            </template>
          </span>
        </template>

        <template #[`item.status`]="{ item }">
          <rmr-status-chip
            :label="requestStatus(item.status).label"
            :variant="requestStatus(item.status).variant"
            :tooltip="item.error || ''"
            fixed
          />
        </template>

        <template #[`item.actions`]="{ item }">
          <div class="rmr-data-table__actions">
            <rmr-icon-button
              :icon="icons.mdiEyeOutline"
              tooltip="Voir le détail"
              @click.stop="openDetail(item)"
            />
          </div>
        </template>

        <template #no-data>
          <div class="rmr-data-table__state">
            <div class="rmr-data-table__state-title">Aucune demande</div>
            <div class="rmr-data-table__state-text">
              Les demandes envoyées depuis le formulaire ci-dessus apparaîtront
              ici, avec leur résultat une fois vérifiées.
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card-text>

    <source-request-detail
      v-model="detail.open"
      :request-id="detail.id"
      @settled="fetchRequests(false)"
    />
  </v-card>
</template>

<script>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "@vue/composition-api";
import { mdiEyeOutline, mdiRefresh } from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import RmrStatusChip from "@/components/ui/feedback/RmrStatusChip.vue";
import RmrAutocomplete from "@/components/ui/forms/RmrAutocomplete.vue";
import RmrSelect from "@/components/ui/forms/RmrSelect.vue";
import RmrTableHeader from "@/components/ui/layout/RmrTableHeader.vue";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";
import { formatDateHumanReadableDayHoursMinutes } from "@/utils/dates";

import SourceRequestDetail from "./SourceRequestDetail.vue";
import {
  REQUEST_STATUS,
  isRequestOpen,
  mediaTypeLabel,
  requestStatus,
} from "./labels";

// Le traitement démarre dès l'envoi (relance par le worker toutes les 10 min) :
// 10 s suffisent pour voir une demande basculer sans marteler l'API (cf.
// `PlanningHistory.vue`, 2 s, pour un traitement synchrone). On ne relance que
// tant qu'une ligne est ouverte.
const POLL_DELAY_MS = 10000;

export default {
  name: "SourceRequestHistory",
  components: {
    RmrActionButton,
    RmrAutocomplete,
    RmrIconButton,
    RmrSelect,
    RmrStatusChip,
    RmrTableHeader,
    SourceRequestDetail,
  },
  setup(props, { emit }) {
    const { axiosGet } = useAxios();
    const { store } = useProxy();

    const items = ref([]);
    const total = ref(0);
    const loading = ref(false);
    const options = ref({ page: 1, itemsPerPage: 25 });
    const filters = reactive({ siteId: null, status: null });
    const detail = reactive({ open: false, id: null });
    const openRequestIds = new Set();
    let pollTimer = null;
    let fetchSeq = 0;

    const headers = [
      { text: "Créée le", value: "created_at", width: "160px" },
      { text: "Site", value: "site", sortable: false },
      { text: "Support", value: "media_type", sortable: false, width: "90px" },
      { text: "Par", value: "requested_by", sortable: false },
      { text: "URL", value: "total", sortable: false, width: "80px" },
      {
        text: "Acceptées / refusées",
        value: "counts",
        sortable: false,
        width: "170px",
      },
      { text: "Statut", value: "status", sortable: false, width: "130px" },
      { text: "", value: "actions", sortable: false, width: "64px" },
    ];

    const statusOptions = Object.entries(REQUEST_STATUS).map(
      ([value, { label }]) => ({ value, label })
    );

    // =>  COMPUTED
    const siteList = computed(
      () => store.getters["common/getAccessibleSiteList"] || []
    );

    // =>  METHODS
    const stopPolling = () => {
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
    };

    const fetchRequests = async (withSpinner = true) => {
      stopPolling();
      const seq = ++fetchSeq;
      if (withSpinner) loading.value = true;
      try {
        const { data } = await axiosGet("/news-radar-source-requests/", {
          page: options.value.page,
          limit: options.value.itemsPerPage,
          ...(filters.siteId ? { site_id: filters.siteId } : {}),
          ...(filters.status ? { status: filters.status } : {}),
        });
        // Un filtre a pu changer pendant l'appel : on garde la dernière réponse.
        if (seq !== fetchSeq) return;
        const settled = data.items.some(
          (request) =>
            openRequestIds.has(request.id) && !isRequestOpen(request.status)
        );
        data.items.forEach((request) => {
          if (isRequestOpen(request.status)) {
            openRequestIds.add(request.id);
          } else {
            openRequestIds.delete(request.id);
          }
        });
        items.value = data.items;
        total.value = data.total;
        if (settled) {
          emit("settled");
        }
        if (data.items.some((request) => isRequestOpen(request.status))) {
          pollTimer = setTimeout(() => fetchRequests(false), POLL_DELAY_MS);
        }
      } catch (error) {
        console.error("Error fetching source requests:", error);
      } finally {
        if (seq === fetchSeq) loading.value = false;
      }
    };

    const refresh = (createdRequest) => {
      if (createdRequest && isRequestOpen(createdRequest.status)) {
        openRequestIds.add(createdRequest.id);
      }
      return fetchRequests();
    };

    const openDetail = (request) => {
      detail.id = request.id;
      detail.open = true;
    };

    // =>  WATCH
    watch(
      () => [options.value.page, options.value.itemsPerPage],
      () => fetchRequests()
    );
    watch(
      () => [filters.siteId, filters.status],
      () => {
        if (options.value.page !== 1) {
          // Le watcher sur la page relance la requête.
          options.value = { ...options.value, page: 1 };
        } else {
          fetchRequests();
        }
      }
    );

    // =>  HOOKS
    onMounted(() => {
      store.dispatch("common/getSiteList");
      fetchRequests();
    });
    onBeforeUnmount(stopPolling);

    // =>  DATA
    return {
      detail,
      fetchRequests,
      filters,
      formatDateHumanReadableDayHoursMinutes,
      headers,
      icons: { mdiEyeOutline, mdiRefresh },
      items,
      loading,
      mediaTypeLabel,
      openDetail,
      options,
      refresh,
      requestStatus,
      siteList,
      statusOptions,
      total,
    };
  },
};
</script>

<style lang="scss" scoped>
.srq-history__site {
  min-width: 260px;
}

.srq-history__counts {
  white-space: nowrap;
}

.srq-history__sep {
  margin: 0 4px;
  color: $rmr-text-muted;
}

.srq-history__ok {
  color: $rmr-success;
}

.srq-history__ko {
  color: $rmr-orange;
}

.srq-history__err {
  color: $rmr-danger;
}
</style>
